import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { getBranchOverview } from "../../services/branch";
import { BranchOverview } from "../../types/branch.type";
import { resolveReadinessStatus } from "./branchStatus";
import { branchDetailPath } from "./constants";
import ReadinessChip from "./ReadinessChip";

const COLUMN_COUNT = 4;
const SKELETON_ROW_COUNT = 5;

interface BranchRowProps {
  branch: BranchOverview;
  onSelect: (uuid: string) => void;
}

function BranchRow({ branch, onSelect }: BranchRowProps) {
  const handleClick = () => onSelect(branch.uuid);
  const routeCount = `${branch.route_open_count} / ${branch.route_total_count}`;
  const status = resolveReadinessStatus(branch.readiness, branch.is_head_office);

  return (
    <TableRow hover onClick={handleClick} sx={{ cursor: "pointer" }}>
      <TableCell>
        <Typography variant="body2" fontWeight={600} color="primary">
          {branch.branch_code}
        </Typography>
      </TableCell>
      <TableCell>{branch.branch_name}</TableCell>
      <TableCell align="center">{routeCount}</TableCell>
      <TableCell align="center">
        <ReadinessChip view={status} />
      </TableCell>
    </TableRow>
  );
}

/**
 * Every branch the viewer may see, ordered by code (master: all; branch manager: their
 * own). The list is short — one row per branch — so it is not paginated.
 */
export default function BranchTable() {
  const navigate = useNavigate();
  const [branches, setBranches] = useState<BranchOverview[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchBranches = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getBranchOverview();
      setBranches(data ?? []);
    } catch {
      // The interceptor has already shown the failure; the table falls back to its empty row.
      setBranches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  const handleSelect = (uuid: string) => navigate(branchDetailPath(uuid));

  const skeletonRows = Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => index);
  const skeletonCells = Array.from({ length: COLUMN_COUNT }, (_, index) => index);
  const isEmpty = !loading && branches.length === 0;

  return (
    <Paper sx={{ p: 0, overflow: "hidden" }}>
      <TableContainer>
        <Table>
          <TableHead sx={{ bgcolor: "background.default" }}>
            <TableRow>
              <TableCell>รหัสสาขา</TableCell>
              <TableCell>ชื่อสาขา</TableCell>
              <TableCell align="center">สายเปิดใช้ / ทั้งหมด</TableCell>
              <TableCell align="center">สถานะความพร้อม</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              skeletonRows.map((row) => (
                <TableRow key={`skeleton-${row}`}>
                  {skeletonCells.map((cell) => (
                    <TableCell key={cell}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {isEmpty && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบสาขา
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              branches.map((branch) => (
                <BranchRow key={branch.uuid} branch={branch} onSelect={handleSelect} />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
